/**
 * BPM Tap Tempo Component
 * Allows users to tap to set tempo and fine-tune BPM
 */
import { useState, useCallback, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './ui/button';
import { Slider } from './ui/slider';
import { 
  Music, 
  ChevronUp, 
  ChevronDown,
  RotateCcw,
  X
} from 'lucide-react';

const MIN_BPM = 20;
const MAX_BPM = 300;
const DEFAULT_BPM = 120;
const TAP_HISTORY_SIZE = 8;
const TAP_TIMEOUT = 2000; // Reset taps after 2 seconds of inactivity

export const TapTempoControl = ({ 
  bpm, 
  onBpmChange, 
  className = '',
  compact = false 
}) => {
  const [showPopover, setShowPopover] = useState(false);
  const [tapTimes, setTapTimes] = useState([]);
  const [tapCount, setTapCount] = useState(0);
  const [isFlashing, setIsFlashing] = useState(false);
  const timeoutRef = useRef(null);
  const popoverRef = useRef(null);

  // Calculate BPM from tap times
  const calculateTapBpm = useCallback((times) => {
    if (times.length < 2) return null;
    
    const intervals = [];
    for (let i = 1; i < times.length; i++) {
      intervals.push(times[i] - times[i - 1]);
    }
    
    const avgInterval = intervals.reduce((a, b) => a + b, 0) / intervals.length;
    const calculatedBpm = Math.round(60000 / avgInterval);
    
    return Math.max(MIN_BPM, Math.min(MAX_BPM, calculatedBpm));
  }, []);

  // Handle tap
  const handleTap = useCallback(() => {
    const now = Date.now();
    
    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    // Flash effect
    setIsFlashing(true);
    setTimeout(() => setIsFlashing(false), 100);
    
    setTapTimes(prev => {
      // Check if too much time has passed
      if (prev.length > 0 && now - prev[prev.length - 1] > TAP_TIMEOUT) {
        return [now];
      }
      
      const newTimes = [...prev, now].slice(-TAP_HISTORY_SIZE);
      
      // Calculate and set BPM if we have enough taps
      if (newTimes.length >= 2) {
        const newBpm = calculateTapBpm(newTimes);
        if (newBpm) {
          onBpmChange(newBpm);
        }
      }
      
      return newTimes;
    });
    
    setTapCount(prev => prev + 1);
    
    // Reset after timeout
    timeoutRef.current = setTimeout(() => {
      setTapTimes([]);
      setTapCount(0);
    }, TAP_TIMEOUT);
  }, [calculateTapBpm, onBpmChange]);

  // Reset tap tempo
  const resetTaps = useCallback(() => {
    setTapTimes([]);
    setTapCount(0);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  }, []);

  // Increment/decrement BPM
  const adjustBpm = useCallback((delta) => {
    const newBpm = Math.max(MIN_BPM, Math.min(MAX_BPM, bpm + delta));
    onBpmChange(newBpm);
  }, [bpm, onBpmChange]);

  // Double-click to reset to default
  const handleDoubleClick = useCallback(() => {
    onBpmChange(DEFAULT_BPM);
  }, [onBpmChange]);

  // Scroll to adjust BPM
  const handleWheel = useCallback((e) => {
    e.preventDefault();
    const delta = e.deltaY < 0 ? 1 : -1;
    adjustBpm(delta);
  }, [adjustBpm]);

  // Close popover on outside click
  useEffect(() => {
    if (!showPopover) return;
    
    const handleClickOutside = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        setShowPopover(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showPopover]);

  // Keyboard shortcut for tap (T key when focused)
  useEffect(() => {
    if (!showPopover) return;
    
    const handleKeyDown = (e) => {
      if (e.key === 't' || e.key === 'T' || e.key === ' ') {
        e.preventDefault();
        handleTap();
      } else if (e.key === 'Escape') {
        setShowPopover(false);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        adjustBpm(1);
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        adjustBpm(-1);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showPopover, handleTap, adjustBpm]);

  if (compact) {
    return (
      <div className={`relative ${className}`}>
        <button
          onClick={() => setShowPopover(!showPopover)}
          onDoubleClick={handleDoubleClick}
          onWheel={handleWheel}
          className="flex items-center gap-1 px-2 py-1 rounded hover:bg-zinc-800 transition-colors"
          data-testid="bpm-control"
          title="Click to adjust, scroll to fine-tune, double-click to reset"
        >
          <span className="text-xs text-zinc-500">BPM</span>
          <span className="text-sm font-mono font-medium text-white">{bpm}</span>
        </button>
        
        <AnimatePresence>
          {showPopover && (
            <motion.div
              ref={popoverRef}
              initial={{ opacity: 0, y: -10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -10, scale: 0.95 }}
              className="absolute top-full mt-2 left-1/2 -translate-x-1/2 w-[200px] bg-zinc-900 border border-zinc-700 rounded-lg shadow-xl z-50 overflow-hidden"
              data-testid="bpm-popover"
            >
              <TapTempoPopoverContent
                bpm={bpm}
                onBpmChange={onBpmChange}
                tapCount={tapCount}
                isFlashing={isFlashing}
                onTap={handleTap}
                onReset={resetTaps}
                onClose={() => setShowPopover(false)}
                adjustBpm={adjustBpm}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }

  return (
    <div className={`bg-zinc-900 border border-zinc-700 rounded-lg p-4 ${className}`}>
      <TapTempoFullContent
        bpm={bpm}
        onBpmChange={onBpmChange}
        tapCount={tapCount}
        isFlashing={isFlashing}
        onTap={handleTap}
        onReset={resetTaps}
        adjustBpm={adjustBpm}
      />
    </div>
  );
};

// Popover content (compact)
const TapTempoPopoverContent = ({ 
  bpm, 
  onBpmChange, 
  tapCount, 
  isFlashing,
  onTap, 
  onReset,
  onClose,
  adjustBpm 
}) => (
  <>
    {/* Header */}
    <div className="flex items-center justify-between px-3 py-2 border-b border-zinc-700 bg-zinc-800/50">
      <span className="text-xs font-medium text-zinc-400">Tempo Control</span>
      <Button
        variant="ghost"
        size="sm"
        onClick={onClose}
        className="p-0.5 h-auto text-zinc-500 hover:text-white"
      >
        <X className="w-3.5 h-3.5" />
      </Button>
    </div>
    
    {/* BPM Display with controls */}
    <div className="px-3 py-3">
      <div className="flex items-center justify-between mb-3">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => adjustBpm(-1)}
          className="p-1 h-auto text-zinc-400 hover:text-white"
        >
          <ChevronDown className="w-4 h-4" />
        </Button>
        
        <div className="text-center">
          <div className="text-2xl font-mono font-bold text-white">{bpm}</div>
          <div className="text-[10px] text-zinc-500">BPM</div>
        </div>
        
        <Button
          variant="ghost"
          size="sm"
          onClick={() => adjustBpm(1)}
          className="p-1 h-auto text-zinc-400 hover:text-white"
        >
          <ChevronUp className="w-4 h-4" />
        </Button>
      </div>
      
      {/* Slider */}
      <Slider
        value={[bpm]}
        onValueChange={([v]) => onBpmChange(v)}
        min={MIN_BPM}
        max={MAX_BPM}
        step={1}
        className="mb-3"
      />
      
      {/* Tap Button */}
      <button
        onClick={onTap}
        className={`w-full py-3 rounded-lg font-medium text-sm transition-all ${
          isFlashing
            ? 'bg-orange-500 text-white scale-95'
            : 'bg-zinc-800 hover:bg-zinc-700 text-zinc-300'
        }`}
        data-testid="tap-tempo-btn"
      >
        <div className="flex items-center justify-center gap-2">
          <Music className="w-4 h-4" />
          <span>Tap Tempo</span>
          {tapCount > 0 && (
            <span className="px-1.5 py-0.5 bg-zinc-700 rounded text-[10px]">
              {tapCount}
            </span>
          )}
        </div>
      </button>
      
      {/* Reset */}
      {tapCount > 0 && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onReset}
          className="w-full mt-2 text-xs text-zinc-500 hover:text-zinc-300"
        >
          <RotateCcw className="w-3 h-3 mr-1" />
          Reset taps
        </Button>
      )}
    </div>
    
    {/* Tip */}
    <div className="px-3 py-2 border-t border-zinc-800 bg-zinc-800/30">
      <p className="text-[10px] text-zinc-500 text-center">
        Press T or Space to tap • Arrow keys to adjust
      </p>
    </div>
  </>
);

// Full content (for standalone use)
const TapTempoFullContent = ({ 
  bpm, 
  onBpmChange, 
  tapCount, 
  isFlashing,
  onTap, 
  onReset,
  adjustBpm 
}) => (
  <>
    <div className="flex items-center justify-between mb-4">
      <h3 className="text-sm font-medium text-white">Tempo</h3>
      {tapCount > 0 && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onReset}
          className="text-xs text-zinc-500"
        >
          <RotateCcw className="w-3 h-3 mr-1" />
          Reset
        </Button>
      )}
    </div>
    
    {/* BPM Display */}
    <div className="flex items-center justify-center gap-4 mb-4">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => adjustBpm(-10)}
        className="text-zinc-400"
      >
        -10
      </Button>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => adjustBpm(-1)}
        className="text-zinc-400"
      >
        <ChevronDown className="w-4 h-4" />
      </Button>
      
      <div className="text-center">
        <div className="text-4xl font-mono font-bold text-white">{bpm}</div>
        <div className="text-xs text-zinc-500">BPM</div>
      </div>
      
      <Button
        variant="ghost"
        size="sm"
        onClick={() => adjustBpm(1)}
        className="text-zinc-400"
      >
        <ChevronUp className="w-4 h-4" />
      </Button>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => adjustBpm(10)}
        className="text-zinc-400"
      >
        +10
      </Button>
    </div>
    
    {/* Slider */}
    <Slider
      value={[bpm]}
      onValueChange={([v]) => onBpmChange(v)}
      min={MIN_BPM}
      max={MAX_BPM}
      step={1}
      className="mb-4"
    />
    
    {/* Tap Button */}
    <button
      onClick={onTap}
      className={`w-full py-4 rounded-lg font-medium transition-all ${
        isFlashing
          ? 'bg-orange-500 text-white scale-95'
          : 'bg-zinc-800 hover:bg-zinc-700 text-zinc-300'
      }`}
      data-testid="tap-tempo-btn-full"
    >
      <div className="flex items-center justify-center gap-2">
        <Music className="w-5 h-5" />
        <span>Tap Tempo</span>
        {tapCount > 0 && (
          <span className="px-2 py-1 bg-zinc-700 rounded text-xs">
            {tapCount} taps
          </span>
        )}
      </div>
    </button>
    
    <p className="text-center text-xs text-zinc-500 mt-3">
      Tap the button or press T to set tempo
    </p>
  </>
);

export default TapTempoControl;
